import { type SectionProps } from '@/components/features/Editor/utils';
import { Image } from '@/components/shared/Image';
import { AspectRatio, Box, Container, Flex, Text, type BoxProps } from '@chakra-ui/react';
import { MotionBox } from '@sphere/ui';
import { useScroll, useSpring, useTransform, type MotionValue } from 'framer-motion';
import { rem } from 'polished';
import { useRef, type RefObject } from 'react';

interface Props extends Omit<SectionProps, 'title'>, Omit<BoxProps, 'title'> {
  isPreview?: boolean;
  activeItem?: 'text';
  containerRef?: RefObject<HTMLElement>;
}

export const TextAndImageBlock = ({
  text,
  image,
  isPreview,
  activeItem,
  containerRef,
  tokenIds: _tokenIds,
  ...props
}: Props) => {
  const scrollProgressContainer = useRef(null);

  const { scrollYProgress } = useScroll({
    target: scrollProgressContainer,
    container: containerRef ? containerRef : undefined,
  });

  const scrollProgressWithSpring = useSpring(scrollYProgress, {
    stiffness: 300,
    damping: 40,
    restDelta: 0.001,
  });

  const scale = useTransform(scrollProgressWithSpring as MotionValue<number>, [0, 1], [1, 1.1]);

  if (isPreview)
    return (
      <Box bg="black" color="white" {...props}>
        <AspectRatio ratio={16 / 9}>
          <Image src={image?.src} alt="" objectFit="cover" fill pointerEvents="none" />
        </AspectRatio>
        <Box opacity={0.6} pos="absolute" inset={0} bg="black"></Box>
        <Text
          pos="absolute"
          display="inline"
          bottom={{ base: 'space.32', md: 'space.120' }}
          fontSize={{ base: '2xl', md: '3xl', lg: '5xl' }}
          mx={{ base: 'space.32', md: 'space.120' }}
          bg={activeItem === 'text' ? 'arcticBlue' : 'unset'}
          color={activeItem === 'text' ? 'white' : 'inherit'}
          transition="all 120ms ease-in-out"
          maxW={rem(900)}
          letterSpacing="title"
          lineHeight="sm"
        >
          {text}
        </Text>
      </Box>
    );

  return (
    <Box
      mt={{ base: 'space.40', md: 'space.96' }}
      pos="relative"
      ref={scrollProgressContainer}
      bg="black"
      h="200vh"
      {...props}
    >
      {image?.src && (
        <Box h="100vh" overflow="hidden" pos="sticky" top={0}>
          <MotionBox w="full" h="full" style={{ scale }}>
            <Image src={image.src} alt="" objectFit="cover" fill />
          </MotionBox>
        </Box>
      )}
      <MotionBox
        pos="absolute"
        inset={0}
        bg="black"
        style={{ opacity: scrollProgressWithSpring }}
      />
      <Container variant="breakout">
        <Flex
          align="flex-end"
          h="100%"
          pos="absolute"
          pr={{ base: 'space.24', md: 'space.64' }}
          pl={{ md: 'space.64' }}
          bottom={{ base: 'space.40', md: 'space.96' }}
        >
          <Text
            maxW={rem(900)}
            color="white"
            fontSize={{ base: '2xl', md: '4xl', lg: '5xl' }}
            letterSpacing="title"
            lineHeight="sm"
          >
            {text}
          </Text>
        </Flex>
      </Container>
    </Box>
  );
};
